import { inject, Injectable } from '@angular/core'
import { ApolloQueryResult } from '@apollo/client'
import {
  BehaviorSubject,
  distinctUntilKeyChanged,
  firstValueFrom,
  tap,
} from 'rxjs'
import { map } from 'rxjs/operators'
import { AuditFiltersCatalogService } from './domains/audit-filters-catalog.service'
import { DeviceFiltersCatalogService } from './domains/device-filters-catalog.service'
import { RepairRequestFiltersCatalogService } from './domains/repair-request-filters-catalog.service'
import {
  FetchEngineerGQL,
  FetchEngineerQuery,
} from './graphql/fetch-engineers.types'
import {
  FetchReferentUsersGQL,
  FetchReferentUsersQuery,
} from './graphql/fetch-referent-users.types'
import { FetchUserGQL, FetchUserQuery } from './graphql/fetch-users.types'
import {
  SimpleFilterAuditDataType,
  SimpleFilterConfiguration,
  SimpleFilterDataType,
  SimpleFilterDefaultDataType,
  SimpleFilterDeviceDataType,
  SimpleFilterDocumentDataType,
  SimpleFilterRepairRequestDataType,
  SimpleFilterTaskDataType,
  SimpleFilterType,
} from './interfaces'
import { TaskFiltersCatalogService } from './domains/task-filters-catalog.service'
import {
  FetchAddressesOptionGQL,
  FetchAddressesOptionQuery,
} from './graphql/fetch-addresses.types'
import { DocumentFiltersCatalogService } from './domains/document-filters-catalog.service'
// eslint-disable-next-line @nx/enforce-module-boundaries
import { RoutedFiltersRouteManagerService } from '@wm-front/shared/routed-filters-service'
import { L10nService } from '@wm-front/admin/l10n'
import { GqlPageInfos } from '@wm-front/shared/interfaces'
import { UserRole } from '@wm-front/shared/enums'
import { MechanicTeam } from '@wm-front/admin/graphql'
import { AuthFacadeService } from '@wm-front/client/auth/data-access'
import {
  FetchCustomersOptionsGQL,
  FetchCustomersOptionsQuery,
} from './graphql/fetch-customer-options.types'
import {
  FetchSupplyManagerOptionsGQL,
  FetchSupplyManagerOptionsQuery,
} from './graphql/fetch-supply-manager.types'

/**
 * Will hold the default filter configuration
 */
@Injectable({ providedIn: 'root' })
export class FiltersCatalogService {
  protected _l10nService = inject(L10nService)

  protected _clientAuthService = inject(AuthFacadeService)

  protected _routedFiltersService = inject(RoutedFiltersRouteManagerService)

  protected _repairRequestFiltersCatalogService = inject(
    RepairRequestFiltersCatalogService
  )

  protected _deviceFiltersCatalogService = inject(DeviceFiltersCatalogService)

  protected _auditFilterCatalogService = inject(AuditFiltersCatalogService)

  protected _taskFilterCatalogService = inject(TaskFiltersCatalogService)

  protected _documentFilterCatalogService = inject(
    DocumentFiltersCatalogService
  )

  protected _userQuery = inject(FetchUserGQL)

  protected _referentUserQuery = inject(FetchReferentUsersGQL)

  protected _customerQuery = inject(FetchCustomersOptionsGQL)

  protected _supplyManagerQuery = inject(FetchSupplyManagerOptionsGQL)

  protected _engineerQuery = inject(FetchEngineerGQL)

  protected _addressQuery = inject(FetchAddressesOptionGQL)

  public getConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<any>
  ): SimpleFilterConfiguration<any> {
    const configuration = { width: overrideConfiguration?.width }
    return {
      ...this.getConfigurationFromTheRightDomain(overrideConfiguration),
      ...configuration,
    }
  }

  public getConfigurationFromTheRightDomain(
    overrideConfiguration: SimpleFilterConfiguration<any>
  ): SimpleFilterConfiguration<any> {
    if (
      this.isItRelatedToThisDomain(
        SimpleFilterRepairRequestDataType,
        overrideConfiguration.dataType
      )
    ) {
      return this._repairRequestFiltersCatalogService.getConfiguration(
        overrideConfiguration
      )
    } else if (
      this.isItRelatedToThisDomain(
        SimpleFilterDeviceDataType,
        overrideConfiguration.dataType
      )
    ) {
      return this._deviceFiltersCatalogService.getConfiguration(
        overrideConfiguration
      )
    } else if (
      this.isItRelatedToThisDomain(
        SimpleFilterAuditDataType,
        overrideConfiguration.dataType
      )
    ) {
      return this._auditFilterCatalogService.getConfiguration(
        overrideConfiguration
      )
    } else if (
      this.isItRelatedToThisDomain(
        SimpleFilterTaskDataType,
        overrideConfiguration.dataType
      )
    ) {
      return this._taskFilterCatalogService.getConfiguration(
        overrideConfiguration
      )
    } else if (
      this.isItRelatedToThisDomain(
        SimpleFilterDocumentDataType,
        overrideConfiguration.dataType
      )
    ) {
      return this._documentFilterCatalogService.getConfiguration(
        overrideConfiguration
      )
    } else {
      return this.getDefaultConfiguration(overrideConfiguration)
    }
  }

  public getDefaultConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<any>
  ): SimpleFilterConfiguration<any> {
    if (overrideConfiguration?.dataType !== undefined) {
      switch (overrideConfiguration.dataType) {
        case SimpleFilterDefaultDataType.SEARCH:
          return this.getSearchFilterConfiguration(overrideConfiguration)
        case SimpleFilterDefaultDataType.EXTERNAL_SEARCH:
          return this.getExternalSearchFilterConfiguration(
            overrideConfiguration
          )
        case SimpleFilterDefaultDataType.USERS:
          return this.getUserSearchFilterConfiguration(overrideConfiguration)
        case SimpleFilterDefaultDataType.MECHANICS:
          return this.getMechanicsFilterConfiguration(overrideConfiguration)
        case SimpleFilterDefaultDataType.AUTHENTICATED_USER:
          return this.getAuthenticatedUserFilterConfiguration(
            overrideConfiguration
          )
        case SimpleFilterDefaultDataType.PERSISTENT_BUILDINGS:
          return this.getPersistentBuildingIdsFilterConfiguration(
            overrideConfiguration
          )
        case SimpleFilterDefaultDataType.PERSISTENT_ACCOUNT:
          return this.getPersistentAccountIdsFilterConfiguration(
            overrideConfiguration
          )
        case SimpleFilterDefaultDataType.PERSISTENT_REFERENT_USER:
          return this.getPersistentOpsReferentFilterConfiguration(
            overrideConfiguration
          )
        case SimpleFilterDefaultDataType.PERSISTENT_OPS_MANAGER:
          return this.getPersistentOpsManagerFilterConfiguration(
            overrideConfiguration
          )
        case SimpleFilterDefaultDataType.BRANCH_MANAGERS:
          return this.getBranchManagerSearchFilterConfiguration(
            overrideConfiguration
          )
        case SimpleFilterDefaultDataType.PERSISTENT_SUPPLY_MANAGER:
          return this.getPersistentSupplyManagersFilterConfiguration(
            overrideConfiguration
          )
        case SimpleFilterDefaultDataType.TERRITORIES:
          return this.getTerritoriesFilterConfiguration(overrideConfiguration)
        case SimpleFilterDefaultDataType.BUILDINGS:
          return this.getBuildingsFilterConfiguration(overrideConfiguration)
        case SimpleFilterDefaultDataType.ASSIGNED_MECHANIC:
          return this.getAssignedMechanicsFilterConfiguration()
        case SimpleFilterDefaultDataType.ASSIGNED_TECHNICIAN:
          return this.getAssignedTechniciansFilterConfiguration()
        case SimpleFilterDefaultDataType.ASSIGNED_REPAIRER:
          return this.getAssignedRepairersFilterConfiguration()
        case SimpleFilterDefaultDataType.REFERENT_USERS:
          return this.getReferentUserSearchFilterConfiguration(
            overrideConfiguration
          )
        case SimpleFilterDefaultDataType.ACCOUNT:
          return this.getAccountSearchFilterConfiguration(overrideConfiguration)
        case SimpleFilterDefaultDataType.SUPPLY_MANAGER:
          return this.getSupplyManagerFilterConfiguration(overrideConfiguration)
        case SimpleFilterDefaultDataType.PERIOD:
          return this.getPeriodFilterConfiguration(overrideConfiguration)
        default:
          return overrideConfiguration
      }
    }
    return overrideConfiguration
  }

  private isItRelatedToThisDomain(
    thisDomain: { [key: string]: string },
    dataType?: SimpleFilterDataType
  ): boolean {
    return (
      !!dataType &&
      Object.keys(thisDomain).some((key) => thisDomain[key] === dataType)
    )
  }

  private getSearchFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<void>
  ): SimpleFilterConfiguration<void> {
    return {
      key: 'search',
      display: overrideConfiguration.display || {
        label: $localize`:Placeholder | Search default placeholder@@filtersCatalogSearchPlaceholder:Search`,
      },
      type: SimpleFilterType.TEXT,
      minSearchLength: overrideConfiguration.minSearchLength,
    }
  }

  private getExternalSearchFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<void>
  ): SimpleFilterConfiguration<void> {
    return {
      key: overrideConfiguration.key || 'search',
      type: SimpleFilterType.EXTERNAL,
      externalFilter$: this._routedFiltersService.filters$.pipe(
        distinctUntilKeyChanged('search'),
        map((filters) => filters['search'])
      ),
    }
  }

  private getUserSearchFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<FetchUserQuery>
  ): SimpleFilterConfiguration<any> {
    return {
      key: overrideConfiguration.key || 'users',
      urlName: overrideConfiguration.urlName || 'userIds',
      type: SimpleFilterType.DROPDOWN,
      display: overrideConfiguration.display || {
        label: $localize`:User filter label | label for the users filter@@filtersCatalogUsersLabel:Utilisateurs`,
        placeholder: $localize`:User filter placeholder | placeholder for the user filter@@filtersCatalogUsersPlaceholder:Trouver un utilisateur`,
      },
      dataGetter: this._userQuery,
      dropdownOptionConverter:
        overrideConfiguration.dropdownOptionConverter ||
        ((data: ApolloQueryResult<FetchUserQuery>) => {
          return data.data.usersv2.edges.map(({ node }) => ({
            label: `${node.firstName} ${node.lastName}`,
            value: (node.id as number).toString(),
          }))
        }),
      defaultPageSize: overrideConfiguration.defaultPageSize || 30,
      paginationConverter: (data: ApolloQueryResult<FetchUserQuery>) =>
        data.data.usersv2.pageInfo as GqlPageInfos,
      filters:
        overrideConfiguration.filters ||
        ({
          roles: [UserRole.Admin],
        } as any),
    }
  }

  private getMechanicsFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<FetchEngineerQuery>
  ): SimpleFilterConfiguration<any> {
    return {
      key: overrideConfiguration.key || 'mechanicIds',
      urlName: overrideConfiguration.urlName || 'mechanicIds',
      type: SimpleFilterType.DROPDOWN,
      display: overrideConfiguration.display || {
        label: $localize`:Mechanic filter label | label for the mechanics filter@@filtersCatalogMechanicsLabel:Engineers`,
        placeholder: $localize`:Mechanic filter placeholder | placeholder for the mechanic filter@@filtersCatalogMechanicsPlaceholder:Find a Engineer`,
      },
      dataGetter: this._engineerQuery,
      dropdownOptionConverter:
        overrideConfiguration.dropdownOptionConverter ||
        ((data: ApolloQueryResult<FetchEngineerQuery>) => {
          return data.data.mechanics.edges.map(({ node }) => ({
            label: `${node.firstName} ${node.lastName}`,
            value: node.id,
          }))
        }),
      defaultPageSize: overrideConfiguration.defaultPageSize || 30,
      withoutBackendFiltering: true,
      paginationConverter: (data: ApolloQueryResult<FetchEngineerQuery>) =>
        data.data.mechanics.pageInfo as GqlPageInfos,
      filters: overrideConfiguration.filters || ({} as any),
    }
  }

  private getAssignedMechanicsFilterConfiguration(): SimpleFilterConfiguration<any> {
    return this.getMechanicsFilterConfiguration({
      key: 'supportAssignedMechanicIds',
      urlName: 'supportAssignedMechanicIds',
      dataType: SimpleFilterDefaultDataType.MECHANICS,
      filters: {
        teams: [MechanicTeam.RepairSupport, MechanicTeam.TechSupport],
        territories: [this._l10nService.territoryLocale],
      },
      defaultPageSize: 1000,
      display: {
        label: $localize`:Mechanic filter label | label for the Mechanic filter@@repairRequestsBusinessViewAssignedMechanicFilterLabel:Assigned engineer`,
        placeholder: $localize`:Mechanic filter placeholder | placeholder for the Mechanic filter@@repairRequestsBusinessViewAssignedMechanicPlaceholder:Find an engineer`,
      },
    })
  }

  private getAssignedRepairersFilterConfiguration(): SimpleFilterConfiguration<any> {
    return this.getMechanicsFilterConfiguration({
      key: 'repairSupportAssignedMechanicIds',
      urlName: 'repairSupportAssignedMechanicIds',
      dataType: SimpleFilterDefaultDataType.MECHANICS,
      filters: {
        teams: [MechanicTeam.RepairSupport, MechanicTeam.TechSupport],
        territories: [this._l10nService.territoryLocale],
      },
      defaultPageSize: 1000,
      display: {
        label: $localize`:Mechanic filter label | label for the Mechanic filter@@repairRequestsBusinessViewAssignedRepairerFilterLabel:Repairer`,
        placeholder: $localize`:Mechanic filter placeholder | placeholder for the Mechanic filter@@repairRequestsBusinessViewAssignedRepairerPlaceholder:Trouver un technicien`,
      },
    })
  }

  private getAssignedTechniciansFilterConfiguration(): SimpleFilterConfiguration<any> {
    return this.getMechanicsFilterConfiguration({
      key: 'techSupportAssignedMechanicIds',
      urlName: 'techSupportAssignedMechanicIds',
      dataType: SimpleFilterDefaultDataType.MECHANICS,
      filters: {
        teams: [MechanicTeam.RepairSupport, MechanicTeam.TechSupport],
        territories: [this._l10nService.territoryLocale],
      },
      defaultPageSize: 1000,
      display: {
        label: $localize`:Mechanic filter label | label for the Mechanic filter@@repairRequestsBusinessViewAssignedTechnicianFilterLabel:Technician`,
        placeholder: $localize`:Mechanic filter placeholder | placeholder for the Mechanic filter@@repairRequestsBusinessViewAssignedTechnicianFilterPlaceholder:Trouver un technicien`,
      },
    })
  }

  private getBuildingsFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<FetchEngineerQuery>
  ): SimpleFilterConfiguration<any> {
    return {
      key: overrideConfiguration.key || 'buildingIds',
      urlName: overrideConfiguration.urlName || 'buildingIds',
      type: SimpleFilterType.DROPDOWN,
      display: overrideConfiguration.display || {
        label: $localize`:@@FiltersAddressButton:Addresse`,
        placeholder: $localize`:@@FiltersAutocompleteDropdownAddressPlaceholderLabel:Trouvez une adresse`,
      },
      dataGetter: this._addressQuery,
      dropdownOptionConverter:
        overrideConfiguration.dropdownOptionConverter ||
        ((data: ApolloQueryResult<FetchAddressesOptionQuery>) => {
          return data.data.buildings.edges.map(({ node }) => ({
            label: `${node.name} - ${node.location?.streetAddress}, ${node.location?.city.name} ${node.location?.zipcode}`,
            value: node.id,
            entity: {
              hasIot: node.hasIot,
            },
          }))
        }),
      defaultPageSize: overrideConfiguration.defaultPageSize || 30,
      withoutBackendFiltering:
        overrideConfiguration.withoutBackendFiltering ?? false,
      minSearchLength: overrideConfiguration.minSearchLength,
      paginationConverter: (
        data: ApolloQueryResult<FetchAddressesOptionQuery>
      ) => data.data.buildings.pageInfo as GqlPageInfos,
      filters: overrideConfiguration.filters || ({} as any),
      bodyConverter: overrideConfiguration.bodyConverter
        ? overrideConfiguration.bodyConverter
        : (filters, state, externalState, _, reset, pagination) => {
            filters = { ...filters }
            if (externalState?.['accountIds']?.length) {
              filters.accountIds = externalState['accountIds']?.map((e) =>
                parseInt(e)
              )
            }
            filters.withContract = true
            return {
              filters,
              pagination,
            }
          },
      fetchSelectedDataBodyConverter:
        overrideConfiguration.fetchSelectedDataBodyConverter
          ? overrideConfiguration.fetchSelectedDataBodyConverter
          : async (value) => {
              if (value?.length) {
                return {
                  filters: { buildingIds: value },
                }
              }
              return undefined
            },
      disabled: overrideConfiguration.disabled,
    }
  }

  private getPersistentAccountIdsFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<void>
  ): SimpleFilterConfiguration<void> {
    return {
      key: overrideConfiguration.key || 'accountIds',
      type: SimpleFilterType.EXTERNAL,
      externalFilter$: this._routedFiltersService.filters$.pipe(
        map((filters) => filters['persistentAccountIds']?.split(','))
      ),
    }
  }

  private getPersistentBuildingIdsFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<void>
  ): SimpleFilterConfiguration<void> {
    return {
      key: overrideConfiguration.key || 'buildingIds',
      type: SimpleFilterType.EXTERNAL,
      externalFilter$: this._routedFiltersService.filters$.pipe(
        map((filters) => filters['persistentBuildingIds']?.split(','))
      ),
    }
  }

  private getPersistentSupplyManagersFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<void>
  ): SimpleFilterConfiguration<void> {
    return {
      key: overrideConfiguration.key || 'supplyManagerEmails',
      type: SimpleFilterType.EXTERNAL,
      externalFilter$: this._routedFiltersService.filters$.pipe(
        map((filters) => filters['persistentSupplyManager']?.split(','))
      ),
    }
  }

  private getPersistentOpsReferentFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<void>
  ): SimpleFilterConfiguration<void> {
    return {
      key: overrideConfiguration.key || 'opsReferentUserIds',
      type: SimpleFilterType.EXTERNAL,
      externalFilter$: this._routedFiltersService.filters$.pipe(
        map((filters) => filters['persistentReferentUserIds']?.split(','))
      ),
    }
  }

  private getPersistentOpsManagerFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<void>
  ): SimpleFilterConfiguration<void> {
    return {
      key: overrideConfiguration.key || 'opsManagerUserIds',
      type: SimpleFilterType.EXTERNAL,
      externalFilter$: this._routedFiltersService.filters$.pipe(
        map((filters) => filters['persistentManagerUserIds']?.split(','))
      ),
    }
  }

  private getBranchManagerSearchFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<FetchUserQuery>
  ): SimpleFilterConfiguration<any> {
    return {
      key: overrideConfiguration.key || 'persistentManagerUserIds',
      urlName: overrideConfiguration.urlName || 'persistentManagerUserIds',
      type: SimpleFilterType.DROPDOWN,
      display: overrideConfiguration.display || {
        label: $localize`:Branch manager filter label | label for the branch manager filter@@filtersCatalogBranchManagersLabel:Branch manager`,
        placeholder: $localize`:Branch manager filter placeholder | placeholder for the branch manager filter@@filtersCatalogBranchManagersPlaceholder:Trouvez un branch manager`,
      },
      dataGetter: this._userQuery,
      dropdownOptionConverter: overrideConfiguration.dropdownOptionConverter
        ? overrideConfiguration.dropdownOptionConverter
        : (data: ApolloQueryResult<FetchUserQuery>) => {
            return data.data.usersv2.edges.map(({ node }) => ({
              label: `${node.firstName} ${node.lastName}`,
              value: (node.id as number).toString(),
            }))
          },
      defaultPageSize: overrideConfiguration.defaultPageSize || 1000,
      withoutBackendFiltering: true,
      paginationConverter: (data: ApolloQueryResult<FetchUserQuery>) =>
        data.data.usersv2.pageInfo as GqlPageInfos,
      filters:
        overrideConfiguration.filters ||
        ({
          roles: [UserRole.Admin],
          isBranchManager: true,
          countryCodes: [this._l10nService.territory$()],
        } as any),
    }
  }

  private getTerritoriesFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<void>
  ): SimpleFilterConfiguration<void> {
    return {
      key: overrideConfiguration.key || 'territories',
      type: SimpleFilterType.EXTERNAL,
      externalFilter$:
        overrideConfiguration.externalFilter$ ||
        new BehaviorSubject(this._l10nService.territoryLocale),
    }
  }

  private getReferentUserSearchFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<FetchUserQuery>
  ): SimpleFilterConfiguration<any> {
    return {
      key: overrideConfiguration.key || 'referentUsers',
      urlName: overrideConfiguration.urlName || 'referentUsersIds',
      type: SimpleFilterType.DROPDOWN,
      display: overrideConfiguration.display || {
        label: $localize`:User filter label | label for the users filter@@filtersCatalogReferenceUsersLabel:Utilisateurs`,
        placeholder: $localize`:User filter placeholder | placeholder for the user filter@@filtersCatalogReferenceUsersPlaceholder:Trouver un utilisateur`,
      },
      dataGetter: this._userQuery,
      dropdownOptionConverter: overrideConfiguration.dropdownOptionConverter
        ? overrideConfiguration.dropdownOptionConverter
        : (data: ApolloQueryResult<FetchUserQuery>) => {
            return data.data.usersv2.edges.map(({ node }) => ({
              label: `${node.firstName} ${node.lastName}`,
              value: (node.id as number).toString(),
            }))
          },
      defaultPageSize: overrideConfiguration.defaultPageSize || 1000,
      withoutBackendFiltering: true,
      paginationConverter: (data: ApolloQueryResult<FetchUserQuery>) =>
        data.data.usersv2.pageInfo as GqlPageInfos,
      filters:
        overrideConfiguration.filters ||
        ({
          countryCodes: [this._l10nService.territory$()],
          isReferentUserOfMechanic: true,
          roles: [UserRole.Admin],
        } as any),
    }
  }

  private getAccountSearchFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<FetchCustomersOptionsQuery>
  ): SimpleFilterConfiguration<any> {
    return {
      key: overrideConfiguration.key || 'accountIds',
      urlName: overrideConfiguration.urlName || 'accountIds',
      type: SimpleFilterType.DROPDOWN,
      display: overrideConfiguration.display || {
        label: $localize`:@@FiltersCustomerButton:Clients`,
        placeholder: $localize`:@@FiltersAutocompleteDropdownCustomerPlaceholderLabel:Trouver un client`,
        emptyResult: $localize`:@@FiltersAutocompleteDropdownCustomerNotFoundOptionLabel:Aucun client trouvé`,
      },
      dataGetter: this._customerQuery,
      dropdownOptionConverter: overrideConfiguration.dropdownOptionConverter
        ? overrideConfiguration.dropdownOptionConverter
        : (data: ApolloQueryResult<FetchCustomersOptionsQuery>) => {
            return data.data.customers.edges.map(({ node }) => ({
              label: `${node.companyName}`,
              value: node.id,
            }))
          },
      defaultPageSize: overrideConfiguration.defaultPageSize || 30,
      paginationConverter: (
        data: ApolloQueryResult<FetchCustomersOptionsQuery>
      ) => data.data.customers.pageInfo as GqlPageInfos,
      filters:
        overrideConfiguration.filters ||
        ({
          territories: [this._l10nService.territory$()],
        } as any),
      fetchSelectedDataBodyConverter: async (value: string[]) => {
        if (value?.length) {
          return {
            filters: {
              accountIds: value,
              territories: [this._l10nService.territory$()],
            } as any,
          }
        }
        return undefined
      },
    }
  }

  private getSupplyManagerFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<FetchSupplyManagerOptionsQuery>
  ): SimpleFilterConfiguration<any> {
    return {
      key: overrideConfiguration.key || 'supplyManager',
      urlName: overrideConfiguration.urlName || 'supplyManager',
      type: SimpleFilterType.DROPDOWN,
      display: overrideConfiguration.display || {
        label: $localize`:@@FiltersSupplyManagerButton:Supply Manager`,
        placeholder: $localize`:@@FiltersAutocompleteDropdownSupplyManagerPlaceholderLabel:Trouvez un responsable`,
        emptyResult: $localize`:@@FiltersAutocompleteDropdownSupplyManagerNotFoundOptionLabel:Aucun responsable trouvé`,
      },
      dataGetter: this._supplyManagerQuery,
      dropdownOptionConverter: overrideConfiguration.dropdownOptionConverter
        ? overrideConfiguration.dropdownOptionConverter
        : (data: ApolloQueryResult<FetchSupplyManagerOptionsQuery>) => {
            return data.data.repairRequestSupplyManagers.edges.map(
              ({ node }) => ({
                label: `${node.email}`,
                value: node.email,
              })
            )
          },
      defaultPageSize: overrideConfiguration.defaultPageSize || 1000,
      paginationConverter: (
        data: ApolloQueryResult<FetchSupplyManagerOptionsQuery>
      ) => data.data.repairRequestSupplyManagers.pageInfo as GqlPageInfos,
    }
  }

  private getPeriodFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<void>
  ): SimpleFilterConfiguration<void> {
    return {
      key: overrideConfiguration.key || 'period',
      urlName: overrideConfiguration.urlName || 'period',
      display: overrideConfiguration.display || {
        label: $localize`:@@FiltersPeriodWithIconCalendarButton:Date`,
      },
      type: SimpleFilterType.PERIOD,
      valueConverter: overrideConfiguration.valueConverter,
      filterConverter: overrideConfiguration.filterConverter,
      disabled: overrideConfiguration.disabled,
    }
  }

  private getAuthenticatedUserFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<void>
  ): SimpleFilterConfiguration<void> {
    return {
      key: 'accountIds',
      type: SimpleFilterType.EXTERNAL,
      externalFilter$:
        overrideConfiguration.externalFilter$ ||
        this._clientAuthService.accountIds$.pipe(
          map((res) => res?.map((id) => id.toString()) || [])
        ),
    }
  }
}
